import type { ComponentProps, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ArrowRight } from "lucide-react";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Tone treatments for {@link Tile}, one per entry in the design system's
 * brand-derived tint scale. Each maps a background tier to its matching
 * readable ink ("strong ink on a soft surface"). Loud `*-ink` / `*-vivid`
 * fills are reserved for the single focal action on a screen.
 */
const tileVariants = cva(
  "group/tile relative flex min-h-0 min-w-0 cursor-pointer appearance-none flex-col overflow-hidden rounded-3xl border-0 px-6.5 py-5.5 text-left outline-none transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] focus-visible:ring-4 focus-visible:ring-ring/45 active:brightness-[0.98] disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-foreground-subtle disabled:text-foreground motion-safe:hover:-translate-y-0.5 motion-safe:active:translate-y-0",
  {
    variants: {
      tone: {
        "blue-ink": "bg-primary text-primary-foreground",
        "blue-vivid": "bg-primary-vivid text-primary-ink",
        "blue-subtle": "bg-primary-subtle text-primary-ink",
        "blue-soft": "bg-primary-soft text-primary-ink",
        "orange-ink": "bg-brand-accent text-brand-accent-foreground",
        "orange-vivid": "bg-brand-accent-vivid text-brand-accent-ink",
        "orange-subtle": "bg-brand-accent-subtle text-brand-accent-ink",
        "orange-soft": "bg-brand-accent-soft text-brand-accent-ink",
        "sky-ink": "bg-brand-supporting text-brand-supporting-foreground",
        "sky-vivid": "bg-brand-supporting-vivid text-brand-supporting-ink",
        "sky-subtle": "bg-brand-supporting-subtle text-brand-supporting-ink",
        "sky-soft": "bg-brand-supporting-soft text-brand-supporting-ink",
        paper: "bg-foreground-soft text-foreground",
        stone: "bg-foreground-subtle text-foreground",
        navy: "bg-foreground text-background",
        "success-vivid": "bg-success-vivid text-success-ink",
        "success-subtle": "bg-success-subtle text-success-ink",
        "red-subtle": "bg-destructive-subtle text-destructive-ink",
        "red-vivid": "bg-destructive-vivid text-destructive-ink",
      },
      size: {
        default: "",
        hero: "px-8.5 py-7.5",
        band: "flex-row items-center gap-4.5 px-5.5 py-3.5",
      },
    },
    defaultVariants: { tone: "blue-subtle", size: "default" },
  },
);

/** The tint pair a {@link Tile} wears. */
export type TileTone = NonNullable<VariantProps<typeof tileVariants>["tone"]>;
/** Size variant of a {@link Tile}: default card, large `hero`, or slim `band`. */
export type TileSize = NonNullable<VariantProps<typeof tileVariants>["size"]>;
type TileIconSize = "default" | "large";

/** Tones whose corner-icon chip needs a slightly stronger fill to read on a saturated fill. */
const INK_TONES = new Set<TileTone>(["blue-ink", "orange-ink", "sky-ink", "navy"]);

export interface TileProps
  extends Omit<ComponentProps<"button">, "children" | "name">,
    VariantProps<typeof tileVariants> {
  /** Display-font headline — the tile's primary label. */
  name: ReactNode;
  /** Uppercase eyebrow shown above the name. */
  label?: ReactNode;
  /** Supporting one-to-two line description. */
  desc?: ReactNode;
  /**
   * Icon (a lucide or `@ascleo/icons` element) shown in a tinted chip beside
   * the text. The chip sits in normal flow, so it reserves its own width and
   * cannot overlap the headline however narrow the tile's column becomes.
   */
  icon?: ReactNode;
  /** Visual scale for the corner icon chip. Use `large` for hub action cards. */
  iconSize?: TileIconSize;
  /**
   * Footer hint shown beside the nudge arrow (ignored on the `band` size).
   * Pass an already-translated string — the Tile carries no copy of its own.
   */
  footer?: ReactNode;
}

/**
 * The repeated visual unit of the FrontDesk+ self-service kiosk: a rounded,
 * tinted action surface with a corner icon chip, optional uppercase label, a
 * display-font name, an optional description, and a footer row whose circular
 * arrow nudges right on hover. `tone` selects the tint pair; `size` switches
 * between the default card, a large `hero`, and a slim full-width `band`.
 *
 * Purely presentational and copy-free: pass translated strings in. Hover/press
 * motion is gated behind `prefers-reduced-motion`.
 *
 * @example
 * ```tsx
 * <Tile
 *   tone="blue-ink"
 *   size="hero"
 *   label={t("self-service.start.check-in-label")}
 *   name={t("self-service.start.check-in-cta")}
 *   desc={t("self-service.start.check-in-description")}
 *   icon={<CheckInIcon />}
 *   footer={t("self-service.start.check-in-hint")}
 *   data-testid="flow-checkin"
 *   onClick={() => onSelectFlow("checkin")}
 * />
 * ```
 */
export function Tile({
  tone,
  size = "default",
  name,
  label,
  desc,
  icon,
  iconSize = "default",
  footer,
  className,
  type = "button",
  ...props
}: TileProps) {
  const isBand = size === "band";
  // A disabled tile is drawn as a solid neutral surface with dark text and
  // without the "go" arrow, so it neither washes out nor invites a tap (KO-34).
  const isDisabled = props.disabled === true;
  const isInk = tone != null && INK_TONES.has(tone);
  const hasLargeIcon = iconSize === "large";

  // The chip sits in normal flow rather than being absolutely positioned with a
  // matching reservation in the button's right padding. The old pairing only
  // held while both numbers agreed: once a fractional grid column narrowed, the
  // reserved strip ate the headline's width, and any tone or size that skipped
  // the reservation let the headline run under the chip.
  const iconChip = icon ? (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center",
        hasLargeIcon
          ? "size-20 rounded-[calc(var(--spacing)*6)] [&_svg]:size-12"
          : "size-10 rounded-[calc(var(--spacing)*3)] [&_svg]:size-6",
        isInk ? "bg-current/20" : "bg-current/15",
      )}
    >
      {icon}
    </span>
  ) : null;

  return (
    <button
      type={type}
      className={cn(tileVariants({ tone, size }), className)}
      {...props}
    >
      {isBand ? (
        <>
          {iconChip}
          <span className="min-w-0 flex-1">
            <span className="block font-sans text-xl leading-[1.2] font-semibold tracking-[-0.005em] text-balance">
              {name}
            </span>
            {desc ? (
              <span className="mt-0.5 block text-base opacity-[0.85]">
                {desc}
              </span>
            ) : null}
          </span>
        </>
      ) : (
        <>
          <span className="flex min-w-0 items-start gap-4">
            <span className="block min-w-0 flex-1">
              {label ? (
                <span className="mb-2 block text-xs font-medium tracking-[0.12em] uppercase opacity-70">
                  {label}
                </span>
              ) : null}
              <span
                className={cn(
                  "block font-heading font-normal leading-[1.05] tracking-[-0.02em] text-balance",
                  size === "hero" ? "text-5xl" : "text-3xl",
                )}
              >
                {name}
              </span>
              {desc ? (
                <span
                  className={cn(
                    "mt-2.5 block text-lg leading-[1.4]",
                    // A disabled tile already draws dark text on a solid neutral surface;
                    // only an enabled tile de-emphasises its description.
                    !isDisabled && "opacity-[0.85]",
                    size === "hero" ? "max-w-[36ch]" : "max-w-[32ch]",
                  )}
                >
                  {desc}
                </span>
              ) : null}
            </span>
            {iconChip}
          </span>
          {isDisabled ? (
            footer ? (
              <span className="mt-auto pt-4.5 text-base font-medium">{footer}</span>
            ) : null
          ) : (
            <span className="mt-auto flex items-center justify-between gap-3 pt-4.5 text-base font-medium">
              <span className="min-w-0">{footer}</span>
              <span className="grid size-11 shrink-0 place-items-center rounded-full bg-current/15 transition-transform duration-200 [&_svg]:size-5 motion-safe:group-hover/tile:translate-x-1">
                <ArrowRight aria-hidden />
              </span>
            </span>
          )}
        </>
      )}
    </button>
  );
}
